import { useState, useCallback, useEffect, useRef } from "react";
import { X, Send, MousePointerClick } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface ElementPickerOverlayProps {
  onPick: (elementInfo: string, question?: string) => void;
  onCancel: () => void;
}

const getElementDescription = (el: HTMLElement): string => {
  const tag = el.tagName.toLowerCase();
  const text = el.innerText?.slice(0, 120)?.trim() || "";
  const ariaLabel = el.getAttribute("aria-label") || "";
  const title = el.getAttribute("title") || "";
  const placeholder = el.getAttribute("placeholder") || "";
  const role = el.getAttribute("role") || "";
  const className = el.className?.toString()?.slice(0, 80) || "";

  const parts: string[] = [];
  if (role) parts.push(`Rolle: ${role}`);
  if (ariaLabel) parts.push(`Label: "${ariaLabel}"`);
  if (title) parts.push(`Titel: "${title}"`);
  if (placeholder) parts.push(`Platzhalter: "${placeholder}"`);
  if (text) parts.push(`Text: "${text.length > 80 ? text.slice(0, 80) + "…" : text}"`);
  parts.push(`Element: <${tag}>`);
  if (className) parts.push(`Klassen: ${className}`);

  return parts.join(" | ");
};

// Walk up to find a meaningful element (button, link, card, section, etc.)
const findMeaningfulElement = (el: HTMLElement): HTMLElement => {
  const meaningfulTags = ["BUTTON", "A", "INPUT", "SELECT", "TEXTAREA", "SECTION", "ARTICLE", "NAV", "HEADER", "MAIN", "ASIDE", "FORM"];
  const meaningfulRoles = ["button", "link", "tab", "menuitem", "dialog", "listitem", "option"];

  let current: HTMLElement | null = el;
  // First pass: look for something meaningful up to 5 levels
  for (let i = 0; i < 5 && current; i++) {
    if (meaningfulTags.includes(current.tagName)) return current;
    if (current.getAttribute("role") && meaningfulRoles.includes(current.getAttribute("role")!)) return current;
    if (current.getAttribute("data-radix-collection-item") !== null) return current;
    current = current.parentElement;
  }
  return el;
};

export const ElementPickerOverlay = ({ onPick, onCancel }: ElementPickerOverlayProps) => {
  const [hoveredElement, setHoveredElement] = useState<HTMLElement | null>(null);
  const [pickedElement, setPickedElement] = useState<HTMLElement | null>(null);
  const [pickedInfo, setPickedInfo] = useState("");
  const [question, setQuestion] = useState("");
  const [confirmPos, setConfirmPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const overlayRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (pickedElement) return;
    // Hide overlay temporarily to get element underneath
    if (overlayRef.current) overlayRef.current.style.pointerEvents = "none";
    if (highlightRef.current) highlightRef.current.style.pointerEvents = "none";
    const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
    if (overlayRef.current) overlayRef.current.style.pointerEvents = "auto";
    if (highlightRef.current) highlightRef.current.style.pointerEvents = "auto";

    if (el && !el.closest("[data-picker-overlay]")) {
      const meaningful = findMeaningfulElement(el);
      setHoveredElement(meaningful);
    } else {
      setHoveredElement(null);
    }
  }, [pickedElement]);

  const handleClick = useCallback((e: MouseEvent) => {
    // Don't intercept clicks inside the picker confirmation dialog (buttons, inputs)
    const target = e.target as HTMLElement;
    const closestOverlay = target.closest("[data-picker-overlay]");
    // Allow clicks on confirmation dialog elements (but not the full-screen backdrop)
    if (closestOverlay && closestOverlay !== overlayRef.current && closestOverlay !== highlightRef.current) return;

    e.preventDefault();
    e.stopPropagation();
    if (pickedElement || !hoveredElement) return;

    const meaningful = findMeaningfulElement(hoveredElement);
    const rect = meaningful.getBoundingClientRect();
    const info = getElementDescription(meaningful);
    setPickedElement(meaningful);
    setPickedInfo(info);

    // Position confirm dialog near the element
    const top = Math.min(rect.bottom + 8, window.innerHeight - 220);
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - 320));
    setConfirmPos({ top, left });
  }, [hoveredElement, pickedElement]);

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove, true);
    document.addEventListener("click", handleClick, true);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove, true);
      document.removeEventListener("click", handleClick, true);
    };
  }, [handleMouseMove, handleClick]);

  // Highlight rect
  const hRect = (pickedElement || hoveredElement)?.getBoundingClientRect();

  const handleSubmit = () => {
    onPick(pickedInfo, question.trim() || undefined);
  };

  const handleCancel = () => {
    if (pickedElement) {
      setPickedElement(null);
      setPickedInfo("");
      setQuestion("");
    } else {
      onCancel();
    }
  };

  return (
    <>
      {/* Full-screen overlay */}
      <div
        ref={overlayRef}
        data-picker-overlay
        className="fixed inset-0 z-[9999] bg-black/10"
        style={{ cursor: pickedElement ? "default" : "crosshair" }}
      />

      {/* Highlight box */}
      {hRect && (
        <div
          ref={highlightRef}
          data-picker-overlay
          className={cn(
            "fixed z-[10000] border-2 rounded pointer-events-none transition-all duration-75",
            pickedElement ? "border-primary bg-primary/10" : "border-blue-500 bg-blue-500/10"
          )}
          style={{
            top: hRect.top - 2,
            left: hRect.left - 2,
            width: hRect.width + 4,
            height: hRect.height + 4,
          }}
        />
      )}

      {/* Top bar hint */}
      {!pickedElement && (
        <div
          data-picker-overlay
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[10001] bg-background border border-border shadow-lg rounded-full px-4 py-2 flex items-center gap-2 text-sm"
        >
          <MousePointerClick className="h-4 w-4 text-primary" />
          <span>Klicke auf ein Element, um die KI danach zu fragen</span>
          <Button variant="ghost" size="icon" className="h-6 w-6 ml-2" onClick={onCancel}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {/* Confirmation dialog */}
      {pickedElement && (
        <div
          data-picker-overlay
          className="fixed z-[10001] w-[300px] bg-background border border-border shadow-xl rounded-xl p-4 animate-in fade-in slide-in-from-bottom-2 duration-150"
          style={{ top: confirmPos.top, left: confirmPos.left }}
        >
          <p className="text-sm font-medium mb-1">Dieses Element auswählen?</p>
          <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
            {pickedInfo.split(" | ").find(p => p.startsWith("Text:")) || pickedInfo.split(" | ")[0]}
          </p>
          <Input
            value={question}
            onChange={e => setQuestion(e.target.value)}
            placeholder="Optionale Frage dazu…"
            className="text-sm mb-3"
            autoFocus
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1" onClick={handleCancel}>
              Abbrechen
            </Button>
            <Button size="sm" className="flex-1 gap-1" onClick={handleSubmit}>
              <Send className="h-3.5 w-3.5" />
              Fragen
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
